import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  createReport,
  createReportSchedule,
  deleteReport,
  deleteReportSchedule,
  listReports,
  listReportSchedules,
  reorderReports,
  runReportConfig,
  updateReport,
  updateReportSchedule,
  listStatuses,
  listPriorities,
  listAssignees,
  listProjects,
  listDepartments,
  listTeams,
  listFacilityGroups,
  getMe,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { titleCase, formatDate } from "../lib/format";
import { saveBlob, csvBlob } from "../lib/download";
import type { ReportResult, ReportRow } from "../lib/types";
import { PageBar } from "../components/Layout";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { BarChart, LineChart, PieChart, type ChartDatum } from "../components/Charts";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import type { ReportSchedule } from "../lib/types";

/** Recipients are stored as a JSON array string; sort by the joined display text. */
function scheduleEmails(s: ReportSchedule): string {
  try {
    return (JSON.parse(s.recipients_json) as string[]).join(", ");
  } catch {
    return "";
  }
}

const SCHEDULE_SORT: Record<string, SortAccessor<ReportSchedule>> = {
  cron: (s) => s.cron,
  recipients: (s) => scheduleEmails(s),
  last_sent: (s) => s.last_run_at,
  status: (s) => s.enabled,
};

/** Report-kind catalog — drives the builder + which controls each kind supports.
 *  `module` also drives per-type access filtering of the builder list (see
 *  MODULE_PERM): helpdesk → ticket.read, facility → facility.read; work /
 *  action-items / combined are always offered. The server enforces the same. */
const REPORT_KINDS: {
  code: string;
  label: string;
  module: "helpdesk" | "work" | "facility" | "action_items" | "combined";
  interval: boolean;
  range: boolean;
  desc: string;
}[] = [
  { code: "ticket_volume", label: "Ticket volume", module: "helpdesk", interval: true, range: true, desc: "New vs solved tickets over time." },
  { code: "sla_compliance", label: "SLA compliance", module: "helpdesk", interval: false, range: true, desc: "Met vs breached SLA timers + attainment %." },
  { code: "agent_workload", label: "Agent workload", module: "helpdesk", interval: false, range: false, desc: "Open tickets per assignee." },
  { code: "csat", label: "CSAT (satisfaction)", module: "helpdesk", interval: false, range: true, desc: "Survey response rate + satisfaction score, overall and by agent." },
  { code: "work_order_volume", label: "Work order volume", module: "facility", interval: true, range: true, desc: "New vs completed work orders over time." },
  { code: "work_order_detail", label: "Work order detail", module: "facility", interval: false, range: true, desc: "Row-level list of work orders — status, priority, assignee, group, location, created." },
  { code: "project_throughput", label: "Project throughput", module: "work", interval: true, range: true, desc: "Completed tasks per project." },
  { code: "time_by_user", label: "Time by user", module: "work", interval: false, range: true, desc: "Logged hours per user." },
  { code: "action_item_summary", label: "Action item summary", module: "action_items", interval: false, range: false, desc: "Your action items by status (admins: all)." },
  { code: "cross_module_workload", label: "Cross-module workload", module: "combined", interval: false, range: false, desc: "Open tickets + tasks + work orders per user." },
  { code: "work_overview", label: "Work overview (all types)", module: "combined", interval: false, range: false, desc: "Open vs closed across every work type — tickets, action items, change requests, work orders, project tasks." },
  { code: "ticket_detail", label: "Ticket detail", module: "helpdesk", interval: false, range: true, desc: "Row-level list of tickets — status, priority, assignee, requester, dates." },
  { code: "task_detail", label: "Project task detail", module: "work", interval: false, range: true, desc: "Row-level list of project tasks — project, status, priority, assignee, points, due." },
];
const kindMeta = (code: string) => REPORT_KINDS.find((k) => k.code === code);

/** Module → the permission a non-admin needs to BUILD/run kinds in that module.
 *  null = always offered (work, action items, combined). Mirrors the server's
 *  REPORT_KIND_PERM; admins ignore this entirely. */
const MODULE_PERM: Record<string, string | null> = {
  helpdesk: "ticket.read",
  facility: "facility.read",
  work: null,
  action_items: null,
  combined: null,
};

/** Which equality filters each row-level detail report supports (config keys
 *  honoured server-side in reports-db ticketDetail/taskDetail). */
type DetailFilterKey = "status" | "priority" | "assignee" | "project" | "department" | "team" | "group";
const DETAIL_FILTERS: Record<string, DetailFilterKey[]> = {
  ticket_detail: ["status", "priority", "assignee", "department", "team"],
  task_detail: ["project", "status", "priority", "assignee", "department"],
  work_order_detail: ["status", "priority", "assignee", "group"],
};

/** Fixed work-order status / priority value sets (facilities are not tenant-
 *  configurable lookups — they're validated at the edge as literals). Used by
 *  the work_order_detail filter dropdowns. */
const WO_STATUSES = ["new", "in_progress", "on_hold", "done", "cancelled"];
const WO_PRIORITIES = ["low", "normal", "high", "urgent"];

/** Filter controls for the row-level detail reports. Dropdowns are populated
 *  from the tenant's lookups; values are the codes/ids the backend matches on
 *  (status code / priority code / assignee id / project id / department code;
 *  task status is free text). "" clears a filter. */
function DetailFilters({
  kind,
  values,
  onChange,
  disabled,
}: {
  kind: string;
  values: Record<string, unknown>;
  onChange: (key: DetailFilterKey, value: string) => void;
  disabled?: boolean;
}) {
  const keys = DETAIL_FILTERS[kind] ?? [];
  const isWorkOrder = kind === "work_order_detail";
  const statusesApi = useApi((signal) => listStatuses(signal), []);
  const prioritiesApi = useApi((signal) => listPriorities(signal), []);
  const assigneesApi = useApi((signal) => listAssignees(signal), []);
  const projectsApi = useApi((signal) => (keys.includes("project") ? listProjects(false, signal) : Promise.resolve({ projects: [] })), []);
  const departmentsApi = useApi((signal) => listDepartments(signal), []);
  const teamsApi = useApi((signal) => (keys.includes("team") ? listTeams(signal) : Promise.resolve({ teams: [] })), []);
  const groupsApi = useApi((signal) => (keys.includes("group") ? listFacilityGroups(signal) : Promise.resolve({ groups: [] })), []);
  if (!keys.length) return null;

  const val = (k: string) => (typeof values[k] === "string" ? (values[k] as string) : "");
  const sel = (k: DetailFilterKey, label: string, opts: { value: string; label: string }[]) => (
    <label className="report-ctl">
      <span>{label}</span>
      <select value={val(k)} onChange={(e) => onChange(k, e.target.value)} disabled={disabled}>
        <option value="">All</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );

  return (
    <>
      {keys.includes("project")
        ? sel("project", "Project", (projectsApi.data?.projects ?? []).map((p) => ({ value: p.id, label: p.name })))
        : null}
      {keys.includes("status") && kind === "ticket_detail"
        ? sel("status", "Status", (statusesApi.data?.statuses ?? []).map((s) => ({ value: s.code, label: s.name })))
        : null}
      {keys.includes("status") && isWorkOrder
        ? sel("status", "Status", WO_STATUSES.map((s) => ({ value: s, label: titleCase(s.replace(/_/g, " ")) })))
        : null}
      {keys.includes("status") && kind === "task_detail" ? (
        <label className="report-ctl">
          <span>Status</span>
          <input type="text" value={val("status")} onChange={(e) => onChange("status", e.target.value)} placeholder="any" disabled={disabled} style={{ width: 110 }} />
        </label>
      ) : null}
      {keys.includes("priority")
        ? sel(
            "priority",
            "Priority",
            isWorkOrder
              ? WO_PRIORITIES.map((p) => ({ value: p, label: titleCase(p) }))
              : (prioritiesApi.data?.priorities ?? []).map((p) => ({ value: p.code, label: p.name })),
          )
        : null}
      {keys.includes("assignee")
        ? sel("assignee", "Assignee", (assigneesApi.data?.assignees ?? []).map((a) => ({ value: a.id, label: a.name })))
        : null}
      {keys.includes("group")
        ? sel("group", "Group", (groupsApi.data?.groups ?? []).map((g) => ({ value: g.id, label: g.name })))
        : null}
      {keys.includes("department")
        ? sel("department", "Department", (departmentsApi.data?.departments ?? []).map((d) => ({ value: d.code, label: d.name })))
        : null}
      {keys.includes("team")
        ? sel("team", "Team", (teamsApi.data?.teams ?? []).map((tm) => ({ value: tm.id, label: tm.name })))
        : null}
    </>
  );
}

type Viz = "table" | "bar" | "line" | "pie";

// ---- date helpers ----------------------------------------------------------
const dayStart = (d: string) => (d ? Math.floor(new Date(d + "T00:00:00Z").getTime() / 1000) : undefined);
const dayEnd = (d: string) => (d ? Math.floor(new Date(d + "T23:59:59Z").getTime() / 1000) : undefined);

export function ReportsPage() {
  const { data, loading, error, reload } = useApi((signal) => listReports(signal), []);
  const reports = data?.reports ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const selected = reports.find((r) => r.id === selectedId) ?? null;

  // Default to the first report once loaded.
  useEffect(() => {
    if (!selectedId && reports.length) setSelectedId(reports[0]!.id);
  }, [reports, selectedId]);

  // Manual reorder: move the report at `index` one slot in `dir`, persist the
  // new full id order, then reload so the list reflects the saved positions.
  const [reordering, setReordering] = useState(false);
  async function moveReport(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= reports.length || reordering) return;
    const ids = reports.map((r) => r.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    setReordering(true);
    try {
      await reorderReports(ids);
      reload();
    } finally {
      setReordering(false);
    }
  }

  return (
    <>
      <PageBar
        crumbs="Reports"
        actions={
          <button className="btn primary" onClick={() => setBuilding(true)}>
            + New report
          </button>
        }
      />
      <div className="content reports-layout">
        <div className="page-head no-print">
          <div>
            <h1>Reports</h1>
            <div className="sub">Build, run, visualize, and export analytics across your workspace.</div>
          </div>
        </div>

        {loading ? (
          <Loading label="Loading reports…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <div className="reports-grid">
            <aside className="card reports-list no-print">
              <div className="card-head">
                <h3>Saved reports</h3>
                <span className="faint">{reports.length}</span>
              </div>
              <div className="card-body tight">
                {reports.length === 0 ? (
                  <EmptyState>No reports yet. Create one to get started.</EmptyState>
                ) : (
                  <ul className="report-nav">
                    {reports.map((r, i) => (
                      <li key={r.id} className="report-nav-row">
                        <button
                          className={`report-nav-item${selectedId === r.id ? " active" : ""}`}
                          onClick={() => setSelectedId(r.id)}
                        >
                          <span className="report-nav-name">{r.name}</span>
                          <span className="report-nav-kind">{kindMeta(r.kind)?.label ?? r.kind}</span>
                        </button>
                        <span className="report-nav-reorder">
                          <button
                            type="button"
                            className="report-nav-move"
                            aria-label={`Move ${r.name} up`}
                            disabled={i === 0 || reordering}
                            onClick={(e) => { e.stopPropagation(); void moveReport(i, -1); }}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="report-nav-move"
                            aria-label={`Move ${r.name} down`}
                            disabled={i === reports.length - 1 || reordering}
                            onClick={(e) => { e.stopPropagation(); void moveReport(i, 1); }}
                          >
                            ▼
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </aside>

            <section className="reports-main">
              {selected ? (
                <ReportRunner key={selected.id} report={selected} onChanged={reload} onDeleted={() => { setSelectedId(null); reload(); }} />
              ) : (
                <div className="card"><div className="card-body"><EmptyState>Select a report, or create a new one.</EmptyState></div></div>
              )}
            </section>
          </div>
        )}
      </div>

      {building ? (
        <ReportBuilder
          onClose={() => setBuilding(false)}
          onCreated={(id) => {
            setBuilding(false);
            setSelectedId(id);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// Runner — runs a saved report, with a date window, a viz switch, and the
// Export / Print / Save / Delete toolbar.
// ===========================================================================
function ReportRunner({ report, onChanged, onDeleted }: { report: ReportRow; onChanged: () => void; onDeleted: () => void }) {
  const meta = kindMeta(report.kind);
  const savedConfig = useMemo<Record<string, unknown>>(() => {
    try {
      return JSON.parse(report.config_json) as Record<string, unknown>;
    } catch {
      return {};
    }
  }, [report.config_json]);

  // Live, editable run config layered over the saved config.
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [interval, setInterval] = useState<string>(typeof savedConfig["interval"] === "string" ? (savedConfig["interval"] as string) : "week");
  const [viz, setViz] = useState<Viz>("table");
  const [result, setResult] = useState<ReportResult | null>(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Row-level detail filters (status/priority/assignee/project/department),
  // seeded from the saved config.
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const k of DETAIL_FILTERS[report.kind] ?? []) {
      if (typeof savedConfig[k] === "string") init[k] = savedConfig[k] as string;
    }
    return init;
  });

  const updateMut = useMutation(updateReport);
  const deleteMut = useMutation(deleteReport);

  const liveConfig = useMemo<Record<string, unknown>>(() => {
    const cfg: Record<string, unknown> = { ...savedConfig };
    if (meta?.interval) cfg["interval"] = interval;
    if (meta?.range) {
      const f = dayStart(fromDate);
      const t = dayEnd(toDate);
      if (f !== undefined) cfg["from"] = f; else delete cfg["from"];
      if (t !== undefined) cfg["to"] = t; else delete cfg["to"];
    }
    for (const k of DETAIL_FILTERS[report.kind] ?? []) {
      if (filters[k]) cfg[k] = filters[k]; else delete cfg[k];
    }
    return cfg;
  }, [savedConfig, meta, interval, fromDate, toDate, filters, report.kind]);

  // Run whenever the report or its live config changes.
  useEffect(() => {
    const ctrl = new AbortController();
    setRunning(true);
    setErr(null);
    runReportConfig({ kind: report.kind, config: liveConfig }, ctrl.signal)
      .then((res) => {
        setResult(res);
        // Pick a sensible default viz from the engine's hint, once per report.
        setViz((cur) => (cur === "table" && res.meta?.chart ? (res.meta.chart as Viz) : cur));
      })
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setErr(e instanceof Error ? e.message : "Report failed");
      })
      .finally(() => setRunning(false));
    return () => ctrl.abort();
  }, [report.kind, liveConfig]);

  const chartable = result ? deriveChart(result) : null;

  function exportCsv() {
    if (!result) return;
    downloadCsv(result, `${report.name.replace(/[^\w-]+/g, "_")}.csv`);
  }
  function print() {
    window.print();
  }
  async function saveWindow() {
    // Persist the current date window / interval onto the saved report.
    const ok = await updateMut.run(report.id, { config: liveConfig });
    if (ok) onChanged();
  }
  function del() {
    void confirmDialog({ message: `Delete the report "${report.name}"?`, danger: true, confirmLabel: "Delete" }).then((yes) => {
      if (yes) void deleteMut.run(report.id).then((r) => r && onDeleted());
    });
  }

  return (
    <>
    <div className="card report-card report-print">
      <div className="card-head report-toolbar">
        <div>
          <h3>{report.name}</h3>
          <div className="faint" style={{ fontSize: "0.8rem" }}>{meta?.desc ?? report.kind}</div>
        </div>
        <div className="report-actions no-print">
          <button className="btn small" onClick={exportCsv} disabled={!result || !result.rows.length} title="Download as CSV">Export</button>
          <button className="btn small" onClick={print} disabled={!result} title="Print / Save as PDF">Print</button>
          <button className="btn small" onClick={saveWindow} disabled={updateMut.pending} title="Save the current filters to this report">
            {updateMut.pending ? <Spinner label="Saving…" /> : "Save"}
          </button>
          <button className="btn small danger" onClick={del} disabled={deleteMut.pending}>Delete</button>
        </div>
      </div>

      {/* Filters + viz controls */}
      <div className="report-controls no-print">
        {meta?.range ? (
          <>
            <label className="report-ctl">
              <span>From</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="report-ctl">
              <span>To</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </>
        ) : null}
        {meta?.interval ? (
          <label className="report-ctl">
            <span>Interval</span>
            <select value={interval} onChange={(e) => setInterval(e.target.value)}>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
            </select>
          </label>
        ) : null}
        <DetailFilters
          kind={report.kind}
          values={filters}
          onChange={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
          disabled={running}
        />
        <div className="view-switch" role="group" aria-label="Visualization" style={{ marginLeft: "auto" }}>
          {(["table", "bar", "line", "pie"] as Viz[]).map((v) => (
            <button
              key={v}
              className={`view-switch-btn${viz === v ? " active" : ""}`}
              onClick={() => setViz(v)}
              disabled={v !== "table" && !chartable}
              aria-pressed={viz === v}
            >
              {titleCase(v)}
            </button>
          ))}
        </div>
      </div>

      <div className="card-body report-body">
        {/* Print-only header */}
        <div className="print-only report-print-head">
          <h2>{report.name}</h2>
          <div className="faint">
            {fromDate || toDate ? `${fromDate || "…"} → ${toDate || "…"}` : "All time"}
            {meta?.interval ? ` · ${interval}` : ""}
          </div>
        </div>

        {running && !result ? (
          <Loading label="Running report…" />
        ) : err ? (
          <Alert kind="error">{err}</Alert>
        ) : !result ? (
          <EmptyState>No result.</EmptyState>
        ) : result.rows.length === 0 ? (
          <EmptyState>This report returned no rows for the selected window.</EmptyState>
        ) : viz !== "table" && chartable ? (
          <>
            {viz === "bar" ? <BarChart data={chartable.data} seriesKeys={chartable.series} /> : null}
            {viz === "line" ? <LineChart data={chartable.data} seriesKeys={chartable.series} /> : null}
            {viz === "pie" ? <PieChart data={chartable.data} valueKey={chartable.series[0]!} /> : null}
            <details className="report-data-table">
              <summary>Show data table</summary>
              <ResultTable result={result} />
            </details>
          </>
        ) : (
          <ResultTable result={result} />
        )}
      </div>
    </div>
    <ScheduleManager reportId={report.id} />
    </>
  );
}

// ===========================================================================
// Schedule manager — email this report to a recipient list on a cadence.
// Delivery itself runs in ac-cron (MaintenanceDb.runDueDigests).
// ===========================================================================
function ScheduleManager({ reportId }: { reportId: string }) {
  const { data, loading, reload } = useApi((signal) => listReportSchedules(reportId, signal), [reportId]);
  const schedules = data?.schedules ?? [];
  const { rows: sortedSchedules, sort, toggle } = useTableSort(schedules, SCHEDULE_SORT);
  const [cron, setCron] = useState("weekly");
  const [recipients, setRecipients] = useState("");
  const createMut = useMutation(createReportSchedule);
  const toggleMut = useMutation(updateReportSchedule);
  const delMut = useMutation(deleteReportSchedule);

  const parseEmails = (s: string) => s.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);

  async function add() {
    const list = parseEmails(recipients);
    if (!list.length) return;
    const res = await createMut.run(reportId, { cron, recipients: list });
    if (res) {
      setRecipients("");
      reload();
    }
  }

  return (
    <div className="card no-print" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h3>Email delivery</h3>
        <span className="faint">{schedules.length}</span>
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading schedules…" />
        ) : (
          <>
            {schedules.length === 0 ? (
              <p className="sub" style={{ margin: "4px 0 12px" }}>
                No delivery scheduled. Add one to email this report's results automatically.
              </p>
            ) : (
              <table className="data" style={{ marginBottom: 12 }}>
                <thead>
                  <tr>
                    <SortTh field="cron" sort={sort} onSort={toggle}>Frequency</SortTh>
                    <SortTh field="recipients" sort={sort} onSort={toggle}>Recipients</SortTh>
                    <SortTh field="last_sent" sort={sort} onSort={toggle}>Last sent</SortTh>
                    <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {sortedSchedules.map((s) => {
                    const emails = (() => {
                      try {
                        return JSON.parse(s.recipients_json) as string[];
                      } catch {
                        return [];
                      }
                    })();
                    return (
                      <tr key={s.id}>
                        <td>{titleCase(s.cron)}</td>
                        <td className="muted">{emails.join(", ")}</td>
                        <td className="faint">{s.last_run_at ? formatDate(s.last_run_at) : "Never"}</td>
                        <td>
                          <span className={`pill ${s.enabled ? "ok" : ""}`}>{s.enabled ? "Active" : "Paused"}</span>
                        </td>
                        <td className="row-actions">
                          <button
                            className="btn small"
                            disabled={toggleMut.pending}
                            onClick={() => void toggleMut.run(s.id, { enabled: !s.enabled }).then((r) => r && reload())}
                          >
                            {s.enabled ? "Pause" : "Resume"}
                          </button>
                          <button
                            className="btn small danger"
                            disabled={delMut.pending}
                            onClick={() =>
                              void confirmDialog({ message: "Delete this delivery schedule?", danger: true, confirmLabel: "Delete" }).then(
                                (yes) => {
                                  if (yes) void delMut.run(s.id).then((r) => r && reload());
                                },
                              )
                            }
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {createMut.error ? <Alert kind="error">{createMut.error}</Alert> : null}
            <div className="report-controls" style={{ padding: 0, border: "none" }}>
              <label className="report-ctl">
                <span>Frequency</span>
                <select value={cron} onChange={(e) => setCron(e.target.value)}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>
              <label className="report-ctl" style={{ flex: 1, minWidth: 220 }}>
                <span>Recipients (comma-separated)</span>
                <input
                  type="text"
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  placeholder="dean@school.edu, registrar@school.edu"
                />
              </label>
              <button className="btn small primary" type="button" onClick={add} disabled={createMut.pending || !recipients.trim()} style={{ flex: "0 0 auto" }}>
                {createMut.pending ? <Spinner label="Adding…" /> : "Schedule"}
              </button>
            </div>
            <p className="hint" style={{ marginTop: 8 }}>
              Delivery runs hourly server-side and sends at most once per period. The email uses the
              report's saved date window.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Builder — create a new saved report from a kind + initial config.
// ===========================================================================
function ReportBuilder({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  // Per-type access: only offer kinds whose module the user can read. Admins see
  // all; until /me loads, fall back to the full list (the server still enforces).
  const meApi = useApi((signal) => getMe(undefined, signal), []);
  const perms = meApi.data?.permissions;
  const isAdmin = meApi.data?.is_admin === true || meApi.data?.roles?.includes("admin") === true;
  const visibleKinds = useMemo(
    () =>
      REPORT_KINDS.filter((k) => {
        const need = MODULE_PERM[k.module];
        return isAdmin || !perms || !need || perms.includes(need);
      }),
    [perms, isAdmin],
  );

  const [name, setName] = useState("");
  const [kind, setKind] = useState(REPORT_KINDS[0]!.code);
  const [interval, setInterval] = useState("week");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<ReportResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const meta = kindMeta(kind);
  const createMut = useMutation(createReport);

  // If permissions resolve and the selected kind isn't offered to this user,
  // snap to the first kind they CAN build so the form never sits on a hidden one.
  useEffect(() => {
    if (visibleKinds.length && !visibleKinds.some((k) => k.code === kind)) {
      setKind(visibleKinds[0]!.code);
      setPreview(null);
    }
  }, [visibleKinds, kind]);

  const config = useMemo<Record<string, unknown>>(() => {
    const cfg: Record<string, unknown> = {};
    if (meta?.interval) cfg["interval"] = interval;
    if (meta?.range) {
      const f = dayStart(fromDate);
      const t = dayEnd(toDate);
      if (f !== undefined) cfg["from"] = f;
      if (t !== undefined) cfg["to"] = t;
    }
    for (const k of DETAIL_FILTERS[kind] ?? []) {
      if (filters[k]) cfg[k] = filters[k];
    }
    return cfg;
  }, [meta, interval, fromDate, toDate, filters, kind]);

  async function doPreview() {
    setPreviewing(true);
    try {
      setPreview(await runReportConfig({ kind, config }));
    } catch {
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    // The persisted reports.module column is the legacy 3-value set
    // (helpdesk|work|combined); the new UI modules (facility, action_items) are a
    // builder-only discriminator, so collapse them onto 'combined' for storage.
    // The `kind` remains the source of truth for access + dispatch.
    const storeModule =
      meta?.module === "helpdesk" || meta?.module === "work" ? meta.module : "combined";
    const res = await createMut.run({
      name: name.trim(),
      kind,
      module: storeModule,
      config,
    });
    if (res) onCreated(res.report.id);
  }

  return (
    <Modal
      title="New report"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={createMut.pending}>Cancel</button>
          <button className="btn" onClick={doPreview} disabled={previewing} type="button">
            {previewing ? <Spinner label="Running…" /> : "Preview"}
          </button>
          <button className="btn primary" type="submit" form="report-builder" disabled={createMut.pending || !name.trim()}>
            {createMut.pending ? <Spinner label="Saving…" /> : "Save report"}
          </button>
        </>
      }
    >
      {createMut.error ? <Alert kind="error">{createMut.error}</Alert> : null}
      <form id="report-builder" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekly ticket volume" autoFocus required />
        </label>
        <label className="field">
          <span className="label">Report type</span>
          <select value={kind} onChange={(e) => { setKind(e.target.value); setPreview(null); }}>
            {visibleKinds.map((k) => (
              <option key={k.code} value={k.code}>{k.label} — {k.module}</option>
            ))}
          </select>
          <span className="hint">{meta?.desc}</span>
        </label>
        <div className="row">
          {meta?.interval ? (
            <label className="field">
              <span className="label">Interval</span>
              <select value={interval} onChange={(e) => setInterval(e.target.value)}>
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
              </select>
            </label>
          ) : null}
          {meta?.range ? (
            <>
              <label className="field">
                <span className="label">From</span>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label className="field">
                <span className="label">To</span>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
            </>
          ) : null}
        </div>
        {DETAIL_FILTERS[kind] ? (
          <div className="report-controls" style={{ padding: 0, border: "none" }}>
            <DetailFilters
              kind={kind}
              values={filters}
              onChange={(k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPreview(null); }}
            />
          </div>
        ) : null}
      </form>
      {preview ? (
        <div style={{ marginTop: 12 }}>
          <div className="faint" style={{ fontSize: "0.8rem", marginBottom: 6 }}>Preview · {preview.rows.length} row(s)</div>
          {preview.rows.length ? <ResultTable result={preview} /> : <EmptyState>No rows for this configuration.</EmptyState>}
        </div>
      ) : null}
    </Modal>
  );
}

// ===========================================================================
// Shared rendering
// ===========================================================================
function ResultTable({ result }: { result: ReportResult }) {
  const stat = singleStat(result);
  if (stat) {
    return (
      <div>
        <div className="metric">{stat.value.toLocaleString()}</div>
        <div className="muted" style={{ marginTop: 4 }}>{titleCase(stat.label.replace(/_/g, " "))}</div>
      </div>
    );
  }
  const cols = result.columns.length ? result.columns : Object.keys(result.rows[0] ?? {});
  return (
    <table className="data">
      <thead>
        <tr>{cols.map((c) => <th key={c}>{titleCase(c.replace(/_/g, " "))}</th>)}</tr>
      </thead>
      <tbody>
        {result.rows.map((row, i) => (
          <tr key={i}>
            {cols.map((c) => {
              const v = row[c];
              return (
                <td key={c} className={typeof v === "number" ? "num" : undefined}>
                  {v == null ? "—" : typeof v === "number" ? v.toLocaleString() : String(v)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function singleStat(result: ReportResult): { label: string; value: number } | null {
  if (result.rows.length !== 1 || result.columns.length !== 1) return null;
  const col = result.columns[0]!;
  const v = result.rows[0]![col];
  return typeof v === "number" ? { label: col, value: v } : null;
}

/** Derive chart data from a result using engine hints (meta.category/series/
 *  value), falling back to "first non-numeric col = category, numeric cols =
 *  series". Returns null when there's nothing chartable. */
function deriveChart(result: ReportResult): { data: ChartDatum[]; series: string[] } | null {
  const cols = result.columns.length ? result.columns : Object.keys(result.rows[0] ?? {});
  if (!result.rows.length) return null;
  const sample = result.rows[0]!;
  const numericCols = cols.filter((c) => typeof sample[c] === "number");
  const textCols = cols.filter((c) => typeof sample[c] !== "number");
  const category = (result.meta?.category as string) || textCols[0];
  if (!category) return null;
  let series: string[];
  if (Array.isArray(result.meta?.series) && result.meta!.series!.length) series = result.meta!.series as string[];
  else if (result.meta?.value) series = [result.meta.value as string];
  else series = numericCols;
  // Don't chart an id-ish raw total_seconds alongside hours, etc. — keep declared series.
  series = series.filter((s) => numericCols.includes(s));
  if (!series.length) return null;
  const data: ChartDatum[] = result.rows.map((row) => ({
    label: String(row[category] ?? "—"),
    values: series.map((k) => ({ key: k, value: typeof row[k] === "number" ? (row[k] as number) : 0 })),
  }));
  return { data, series };
}

/** Build + download a CSV from a report result. */
function downloadCsv(result: ReportResult, filename: string) {
  const cols = result.columns.length ? result.columns : Object.keys(result.rows[0] ?? {});
  saveBlob(csvBlob([cols, ...result.rows.map((r) => cols.map((c) => r[c] ?? null))]), filename);
}
